<script>
import Layout from '../../layouts/main'
import PageHeader from '@/components/page-header'
import appConfig from "@/app.config";
import axios from "axios";
import { vendorMethods, vendorComputed } from "@/state/helpers";

export default {
  page: {
    title: "Service Providers",
    meta: [{ name: "description", content: appConfig.description }]
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: 'Service Providers',
      items: [
        { text: 'Ecommerce', href: '/' },
        { text: 'Service Providers', active: true },
      ],
      loading: true
    }
  },
  mounted() {
    this.fetchVendors().then(() => {
      this.loading = false;
    });
  },
  computed: {
    ...vendorComputed,
  },
  methods: {
    ...vendorMethods,
    getVendorPhoto(name) {
      // if (!name) return require('@/assets/images/default-provider.jpg');
      let url = axios.defaults.baseURL;
      return (url).slice(0,-3) + "logos/" + name;
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="providers-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-grid">
        <div v-for="i in 8" :key="i" class="provider-card-skeleton">
          <div class="image-skeleton"></div>
          <div class="content-skeleton">
            <div class="title-skeleton"></div>
            <div class="address-skeleton"></div>
          </div>
        </div>
      </div>

      <!-- Providers Grid -->
      <div v-else class="providers-grid">
        <router-link 
          v-for="shop in vendors" 
          :key="shop.id" 
          :to="{ path: '/shop', query: { id: shop.user_id }}"
          class="provider-card"
        >
          <div class="provider-image-container">
            <img 
              :src="getVendorPhoto(shop.logo)" 
              :alt="shop.business_name"
              class="provider-image"
              loading="lazy"
            >
            <div class="provider-badge">
              <i class="mdi mdi-star"></i> Featured
            </div>
          </div>
          <div class="provider-details">
            <h3 class="provider-name">{{ shop.business_name }}</h3>
            <div class="provider-location">
              <i class="mdi mdi-map-marker-outline"></i>
              <span>{{ shop.business_address }}</span>
            </div>
            <div class="provider-rating">
              <div class="stars">
                <i class="mdi mdi-star" v-for="n in 5" :key="n"></i>
              </div>
              <span class="rating-text">4.8 (24 reviews)</span>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && vendors.length === 0" class="empty-state">
        <i class="mdi mdi-store-outline"></i>
        <h3>No Service Providers Found</h3>
        <p>We couldn't find any providers in your area</p>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Color Variables */
:root {
  --primary: #564ab1;
  --primary-light: #6f65c2;
  --primary-dark: #453d8f;
  --secondary: #E6FDFF;
  --text-dark: #2d3748;
  --text-light: #718096;
  --white: #ffffff;
  --gray-light: #f7fafc;
  --gray: #e2e8f0;
  --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --transition: all 0.3s ease;
}

/* Base Container */
.providers-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

/* Loading State */
.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
}

.provider-card-skeleton {
  background: var(--white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow);
}

.image-skeleton {
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.content-skeleton {
  padding: 20px;
}

.title-skeleton {
  height: 24px;
  width: 80%;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 12px;
}

.address-skeleton {
  height: 16px;
  width: 60%;
  background: #f0f0f0;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Providers Grid */
.providers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
}

.provider-card {
  background: var(--white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: var(--transition);
  text-decoration: none;
  color: var(--text-dark);
  display: block;
}

.provider-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.provider-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.provider-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: var(--transition);
}

.provider-card:hover .provider-image {
  transform: scale(1.05);
}

.provider-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: var(--primary);
  color: var(--white);
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.provider-details {
  padding: 20px;
}

.provider-name {
  font-size: 18px;
  margin-bottom: 10px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.provider-location {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-light);
  font-size: 14px;
  margin-bottom: 15px;
}

.provider-rating {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stars {
  color: #FFC107;
}

.rating-text {
  font-size: 13px;
  color: var(--text-light);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: var(--white);
  border-radius: 12px;
  box-shadow: var(--shadow);
}

.empty-state i {
  font-size: 60px;
  color: var(--primary);
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 22px;
  margin-bottom: 10px;
  color: var(--text-dark);
}

.empty-state p {
  color: var(--text-light);
  font-size: 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .providers-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }
  
  .provider-image-container {
    height: 180px;
  }
}

@media (max-width: 576px) {
  .providers-container {
    padding: 15px;
  }
  
  .providers-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}
</style>